import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StudySession, studySessionsApi, questionsApi, Question, QuestionAnswerResponse } from '../../../services/api';
import { Card, SpaceCard } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { ArrowLeft, ArrowRight, HelpCircle, CheckCircle, XCircle, RefreshCw, Target, Sparkles, Trophy, RotateCcw } from 'lucide-react';
import { FlowStep } from '../NewStudySession';
import StudyMaterialRenderer from '../../StudyMaterialRenderer';
import { MixedText } from '../../../utils/rtl';

interface QuestionsStepProps {
  session: StudySession;
  onSessionUpdate: (session: StudySession) => void;
  onStepChange: (step: FlowStep) => void;
  isRTL: boolean;
  onOpenChat?: (message: string) => void;
}

const QuestionsStep: React.FC<QuestionsStepProps> = ({
  session,
  onSessionUpdate,
  onStepChange,
  isRTL,
  onOpenChat
}) => {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<{[questionId: number]: number}>({});
  const [showResults, setShowResults] = useState<{[questionId: number]: QuestionAnswerResponse}>({});
  const [showDifficultyModal, setShowDifficultyModal] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState<'easier' | 'same' | 'harder'>('same');
  const [completing, setCompleting] = useState(false);
  const questionsRef = useRef<HTMLDivElement>(null);

  const hasQuestions = () => Boolean(session?.questions && session.questions.length > 0);
  const hasStudyMaterials = () => Boolean(session?.study_materials && session.study_materials.length > 0);

  const getQuestionsCompletionStatus = () => {
    if (!session?.questions || session.questions.length === 0) return false;
    const answeredCorrectly = Object.values(showResults).filter(result => result.is_correct === true).length;
    const totalQuestions = session.questions.length;
    return totalQuestions > 0 && (answeredCorrectly / totalQuestions) >= 0.8;
  };

  // Progress calculations
  const totalQuestions = session?.questions?.length || 0;
  const answeredQuestions = Object.keys(selectedAnswers).length;
  const progressPercentage = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;

  useEffect(() => {
    // Auto-generate questions if we have materials but no questions
    if (hasStudyMaterials() && !hasQuestions() && !generating) {
      handleGenerateQuestions();
    }
  }, [session]);

  useLayoutEffect(() => {
    // Instantly position at top when questions already exist on mount
    if (hasQuestions()) {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0; // For Safari
    }
  }, []);

  const handleGenerateQuestions = async () => {
    setGenerating(true);
    try {
      const response = await questionsApi.generateQuestions({
        session_id: session.id,
        question_types: ['multiple_choice', 'true_false'],
        num_questions: Math.floor(Math.random() * 6) + 10 // 10-15
      });
      
      // Refresh session
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);

      // Scroll to questions after they're rendered
      setTimeout(() => {
        questionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Error generating questions:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateQuestions = async () => {
    setRegenerating(true);
    try {
      // Collect previous question texts to avoid duplicates
      const previousQuestionTexts = session?.questions?.map(q => q.question_text) || [];
      
      // First delete existing questions
      await questionsApi.deleteSessionQuestions(session.id);
      
      // Then generate new ones
      const response = await questionsApi.generateQuestions({
        session_id: session.id,
        question_types: ['multiple_choice', 'true_false'],
        num_questions: Math.floor(Math.random() * 6) + 10, // 10-15
        difficulty_preference: selectedDifficulty,
        previous_question_texts: previousQuestionTexts.length > 0 ? previousQuestionTexts : undefined
      });
      
      // Reset answers and results
      setSelectedAnswers({});
      setShowResults({});
      setShowDifficultyModal(false);
      
      // Refresh session
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);

      // Scroll to questions after they're rendered
      setTimeout(() => {
        questionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Error regenerating questions:', error);
    } finally {
      setRegenerating(false);
    }
  };

  const handleAnswerQuestion = async (questionId: number, optionId: number) => {
    if (showResults[questionId]) return; // Already answered
    
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));

    try {
      const response = await questionsApi.answerQuestion({
        question_id: questionId,
        selected_option_id: optionId
      });
      
      setShowResults(prev => ({
        ...prev,
        [questionId]: response
      }));
    } catch (error) {
      console.error('Error answering question:', error);
    }
  };

  const handleComplete = async () => {
    if (getQuestionsCompletionStatus()) {
      try {
        setCompleting(true);
        const response = await studySessionsApi.completeSession(session.id, {
          completion_percentage: 100
        });
        onSessionUpdate(response);
        onStepChange('complete');
      } catch (error) {
        console.error('Error completing session:', error);
      } finally {
        setCompleting(false);
      }
    }
  };

  const handleRetryQuestions = () => {
    // Clear all answers and results to allow retry
    setSelectedAnswers({});
    setShowResults({});
  };

  if (generating) {
    return (
      <div className="space-y-8 min-h-screen">
        <div className="text-center">
          <h2 className={`text-2xl font-bold text-bg-primary mb-4 font-hebrew text-center`}>
            {t('sessions:flow.questions.title', 'Practice Questions')}
          </h2>
          <p className={`text-bg-secondary max-w-2xl mx-auto font-hebrew text-center`}>
            {t('sessions:flow.questions.description', 'Test your understanding with AI-generated questions')}
          </p>
        </div>

        <div className="card text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent mx-auto mb-6"></div>
          <h3 className={`text-xl font-semibold text-secondary-900 mb-2 font-hebrew text-center`}>
            <MixedText>{t('sessions:flow.materials.creatingQuestions', 'Creating personalized practice questions based on your study materials')}</MixedText>
          </h3>
          <p className={`text-secondary-600 max-w-md mx-auto font-hebrew text-center`}>
            <MixedText>{t('sessions:flow.materials.generating10to15Questions', 'Generating Questions...')}</MixedText>
          </p>
        </div>
      </div>
    );
  }

  if (!hasQuestions()) {
    return (
      <div className="space-y-8 min-h-screen">
        <div className="text-center">
          <h2 className={`text-2xl font-bold text-bg-primary mb-4 font-hebrew text-center ${isRTL ? 'text-right' : ''}`}>
            {t('sessions:flow.questions.title', 'Practice Questions')}
          </h2>
          <p className={`text-bg-secondary max-w-2xl mx-auto font-hebrew text-center ${isRTL ? 'text-right' : ''}`}>
            {t('sessions:flow.questions.description', 'Test your understanding with AI-generated questions')}
          </p>
        </div>

        <div className="card text-center py-16">
          <HelpCircle className="w-16 h-16 text-secondary-400 mx-auto mb-6" />
          <h3 className={`text-xl font-semibold text-secondary-900 mb-4 font-hebrew text-center ${isRTL ? 'text-right' : ''}`}>
            {t('sessions:flow.questions.noQuestions', 'No Questions Yet')}
          </h3>
          <p className={`text-secondary-600 mb-8 max-w-md mx-auto font-hebrew text-center ${isRTL ? 'text-right' : ''}`}>
            {t('sessions:flow.questions.noQuestionsDescription', 'Generate study materials first to create practice questions')}
          </p>
          <Button
            onClick={() => onStepChange('materials')}
            variant="primary"
          >
            {t('sessions:flow.questions.goToMaterials', 'Go to Study Materials')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 min-h-screen">
      <div className="text-center">
        <h2 className={`text-2xl font-bold text-bg-primary mb-4 font-hebrew text-center`}>
          {t('sessions:flow.questions.title', 'Practice Questions')}
        </h2>
        <p className={`text-bg-secondary max-w-2xl mx-auto font-hebrew text-center`}>
          {t('sessions:flow.questions.description', 'Test your understanding with AI-generated questions')}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="card">
        <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className={`font-semibold text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {t('sessions:flow.questions.progress', 'Progress')}
          </span>
          <span className={`text-sm text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {answeredQuestions}/{totalQuestions} {t('sessions:flow.questions.correct', 'answered')}
          </span>
        </div>
        <div className="w-full bg-muted-purple/20 rounded-full h-3">
          <div 
            className="bg-muted-purple h-3 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Questions */}
      <div ref={questionsRef} className="space-y-6">
        {regenerating && (
          <div className="card border-2 border-purple-200 bg-purple-50">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
                <h3 className={`text-lg font-semibold text-purple-900 mb-2 font-hebrew text-center ${isRTL ? 'text-right' : ''}`}>
                  {t('sessions:flow.questions.regeneratingQuestions', 'Regenerating Questions...')}
                </h3>
                <p className={`text-purple-700 font-hebrew text-center ${isRTL ? 'text-right' : ''}`}>
                  {t('sessions:flow.questions.generatingNewQuestions', 'Generating new questions with your selected difficulty')}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {!regenerating && session.questions?.map((question, index) => (
          <QuestionCard
            key={question.id}
            question={question}
            questionNumber={index + 1}
            selectedAnswer={selectedAnswers[question.id]}
            result={showResults[question.id]}
            onAnswer={handleAnswerQuestion}
            showResult={!!showResults[question.id]}
            isRTL={isRTL}
            onOpenChat={onOpenChat}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          onClick={() => onStepChange('materials')}
          variant="ghost"
          icon={isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          iconPosition="left"
        >
          {t('sessions:flow.questions.backToMaterials', 'Back to Materials')}
        </Button>

        <div className={`flex items-center gap-4 ${isRTL ? 'space-x-reverse' : ''}`}>
          {/* Show regeneration status when in progress */}
          {regenerating && (
            <div className={`flex items-center px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
              <span className={`text-purple-700 font-medium font-hebrew ${isRTL ? 'text-right' : ''}`}>
                {t('sessions:flow.questions.regeneratingQuestions', 'Regenerating Questions...')}
              </span>
            </div>
          )}

          {/* Retry buttons - show when questions are answered but not completed */}
          {!regenerating && Object.keys(showResults).length > 0 && !getQuestionsCompletionStatus() && (
            <Button
              onClick={handleRetryQuestions}
              variant="secondary"
              icon={<RotateCcw className="w-4 h-4" />}
              iconPosition="left"
            >
              {t('sessions:flow.questions.retrySameQuestions', 'Retry Same Questions')}
            </Button>
          )}

          {/* Regenerate button - always show when questions exist */}
          {!regenerating && hasQuestions() && (
            <Button
              onClick={() => setShowDifficultyModal(true)}
              variant="secondary"
              icon={<RefreshCw className="w-4 h-4" />}
              iconPosition="left"
            >
              {t('sessions:flow.questions.generateNewQuestions', 'Generate New Questions')}
            </Button>
          )}

          {/* Complete button - show when all questions answered and not regenerating */}
          {!regenerating && getQuestionsCompletionStatus() && (
            <Button
              onClick={handleComplete}
              disabled={completing}
              variant="primary"
              icon={<Trophy className="w-4 h-4" />}
              iconPosition="left"
              loading={completing}
            >
              {t('sessions:flow.questions.completeLesson', 'Complete Session')}
            </Button>
          )}
        </div>
      </div>

      {/* Difficulty Modal */}
      {showDifficultyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" dir={isRTL ? 'rtl' : 'ltr'}>
            <h3 className={`text-xl font-semibold text-secondary-900 mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
              {t('sessions:flow.questions.selectDifficulty', 'Select Difficulty')}
            </h3>
            <p className={`text-secondary-600 mb-6 font-hebrew ${isRTL ? 'text-right' : ''}`}>
              {t('sessions:flow.questions.difficultyDescription', 'Choose the difficulty level for new questions:')}
            </p>
            
            <div className="space-y-3 mb-6">
              {[
                { value: 'easier' as const, label: t('sessions:flow.questions.easier', 'Easier'), description: t('sessions:flow.questions.easierDesc', 'Simpler questions') },
                { value: 'same' as const, label: t('sessions:flow.questions.same', 'Same Level'), description: t('sessions:flow.questions.sameDesc', 'Similar difficulty') },
                { value: 'harder' as const, label: t('sessions:flow.questions.harder', 'Harder'), description: t('sessions:flow.questions.harderDesc', 'More challenging') }
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedDifficulty === option.value 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-secondary-200 hover:border-primary-300 hover:bg-primary-50'
                  } ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                >
                  <input
                    type="radio"
                    name="difficulty"
                    value={option.value}
                    checked={selectedDifficulty === option.value}
                    onChange={() => setSelectedDifficulty(option.value)}
                    className={`mt-1 ${isRTL ? 'ml-3' : 'mr-3'}`}
                  />
                  <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                    <div className={`font-semibold text-secondary-900 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      {option.label}
                    </div>
                    <div className={`text-sm text-secondary-600 mt-1 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      {option.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            
            <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} gap-3 pt-4`}>
              <Button
                onClick={handleRegenerateQuestions}
                disabled={regenerating}
                variant="primary"
                className="flex-1"
                loading={regenerating}
              >
                {t('sessions:flow.questions.generateQuestions', 'Generate Questions')}
              </Button>
              <Button
                onClick={() => setShowDifficultyModal(false)}
                disabled={regenerating}
                variant="ghost"
                className="flex-1"
              >
                {t('sessions:flow.questions.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface QuestionCardProps {
  question: Question;
  questionNumber: number;
  selectedAnswer?: number;
  result?: QuestionAnswerResponse;
  onAnswer: (questionId: number, optionId: number) => void;
  showResult: boolean;
  isRTL: boolean;
  onOpenChat?: (message: string) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionNumber,
  selectedAnswer,
  result,
  onAnswer,
  showResult,
  isRTL,
  onOpenChat
}) => {
  const { t } = useTranslation();
  
  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-secondary-100 text-secondary-700';
    }
  };

  const getDifficultyLabel = (level: string) => {
    switch (level) {
      case 'easy': return isRTL ? 'קל' : 'Easy';
      case 'medium': return isRTL ? 'בינוני' : 'Medium';
      case 'hard': return isRTL ? 'קשה' : 'Hard';
      default: return level;
    }
  };

  const handleHintRequest = () => {
    if (onOpenChat) {
      const hintMessage = t('sessions:flow.questions.hintRequest', {
        questionNumber
      });
      onOpenChat(hintMessage);
    }
  };

  const handleSolutionRequest = () => {
    if (onOpenChat) {
      const solutionMessage = t('sessions:flow.questions.solutionRequest', {
        questionNumber
      });
      onOpenChat(solutionMessage);
    }
  };

  return (
    <div className={`card transition-all duration-200 ${
      showResult ? (result?.is_correct === true ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50') : ''
    }`}>
      <div className={`flex items-start justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
          <span className={`text-xs px-2 py-1 rounded-full font-hebrew ${getDifficultyColor(question.difficulty_level)} mr-3 ml-3`}>
            {getDifficultyLabel(question.difficulty_level)}
          </span>
          <span className="bg-purple-100 text-purple-700 text-sm font-bold px-3 py-1 rounded-full ml-3 mr-3">
            Q{questionNumber}
          </span>
        </div>

        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          {/* Hint button only */}
          {onOpenChat && (
            <button
              onClick={handleHintRequest}
              className={`group flex items-center px-3 py-2 bg-gradient-to-r from-muted-purple/10 to-muted-purple/20 hover:from-muted-purple/20 hover:to-muted-purple/30 border border-muted-purple/30 hover:border-muted-purple/50 rounded-lg transition-all duration-200 hover:scale-105 ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              title={t('sessions:flow.questions.giveHint')}
            >
              <img
                src="/astronaut_idea.png"
                alt="Hint"
                className="w-6 h-6 object-contain group-hover:scale-110 transition-transform duration-200"
              />
              <span className={`text-xs font-medium text-muted-purple group-hover:text-muted-purple/90 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                {t('sessions:flow.questions.giveHint')}
              </span>
            </button>
          )}

          {/* Result indicator */}
          {showResult && (
            <div className="flex items-center space-x-2">
              {result?.is_correct ? (
                <CheckCircle className="w-5 h mr-3 ml-3 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="text-lg font-medium text-secondary-900 mb-6 leading-relaxed" dir={isRTL ? 'rtl' : 'ltr'}>
        <StudyMaterialRenderer
          content={question.question_text}
          materialType="question"
          isRTL={isRTL}
        />
      </div>

      {/* Answer Options */}
      <div className="space-y-3">
        {question.options.map((option, index) => {
          const isSelected = selectedAnswer === option.id;
          const isCorrect = showResult ? option.is_correct : false;
          const isIncorrect = showResult && isSelected && !result?.is_correct;
          
          let buttonClass = `w-full p-4 border rounded-lg transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'} `;
          
          if (showResult) {
            if (isCorrect) {
              buttonClass += "border-green-500 bg-green-100 text-green-800 ";
            } else if (isIncorrect) {
              buttonClass += "border-red-500 bg-red-100 text-red-800 ";
            } else {
              buttonClass += "border-secondary-200 bg-secondary-50 text-secondary-600 ";
            }
          } else {
            if (isSelected) {
              buttonClass += "border-vibrant-orange bg-vibrant-orange/10 text-vibrant-orange ";
            } else {
              buttonClass += "border-secondary-200 hover:border-vibrant-orange/50 hover:bg-vibrant-orange/5 text-secondary-700 ";
            }
          }
          
          return (
            <button
              key={option.id}
              onClick={() => !showResult && onAnswer(question.id, option.id)}
              disabled={showResult}
              className={buttonClass}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  showResult
                    ? isCorrect
                      ? 'bg-green-500 text-white'
                      : isIncorrect
                      ? 'bg-red-500 text-white'
                      : 'bg-secondary-200 text-secondary-600'
                    : isSelected
                    ? 'bg-vibrant-orange text-white'
                    : 'bg-secondary-200 text-secondary-600'
                }`}>
                  {String.fromCharCode(65 + index)}
                </span>
                <div className="flex-1 font-medium">
                  <StudyMaterialRenderer
                    content={option.option_text}
                    materialType="answer_option"
                    isRTL={isRTL}
                  />
                </div>
                {showResult && isCorrect && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                {showResult && isIncorrect && (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
            </button>
          );
        })}

        {/* Solution button - only show after user selects an answer */}
        {selectedAnswer && onOpenChat && (
          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} mt-4`}>
            <button
              onClick={handleSolutionRequest}
              className={`group flex items-center px-4 py-2 bg-gradient-to-r from-muted-purple/10 to-muted-purple/20 hover:from-muted-purple/20 hover:to-muted-purple/30 border border-muted-purple/30 hover:border-muted-purple/50 rounded-lg transition-all duration-200 hover:scale-105 ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              title={t('sessions:flow.questions.explainSolution')}
            >
              <img
                src="/astronaut_reading.png"
                alt="Solution"
                className="w-5 h-5 object-contain group-hover:scale-110 transition-transform duration-200"
              />
              <span className={`text-sm font-medium text-muted-purple group-hover:text-muted-purple/90 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                {t('sessions:flow.questions.explainSolution')}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Result Explanation */}
      {showResult && result?.explanation && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" dir={isRTL ? 'rtl' : 'ltr'}>
          <h4 className={`font-semibold text-blue-900 mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
            {t('sessions:flow.questions.explanation', 'Explanation')}
          </h4>
          <div className={`text-blue-800 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
            <StudyMaterialRenderer
              content={result.explanation}
              materialType="explanation"
              isRTL={isRTL}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionsStep; 